import { z } from 'zod';
import { VoiceApiStatus } from '../../../conversation/voiceApiStatus.js';
/** Vapi caps assistant `voicemailMessage` at this length.
 * @see https://docs.vapi.ai/api-reference/assistants/create#response.body.voicemailMessage
 */
export const VOICEMAIL_MESSAGE_MAX_LENGTH = 1000;
/** Vapi caps assistant `endCallMessage` at this length.
 * @see https://docs.vapi.ai/api-reference/assistants/create#response.body.endCallMessage
 */
export const END_CALL_MESSAGE_MAX_LENGTH = 1000;
// https://docs.vapi.ai/api-reference/webhooks/server-message#request.body.message.EndOfCallReport.artifact.messagesOpenAIFormatted.role
export var OpenAiMessageRole;
(function (OpenAiMessageRole) {
    OpenAiMessageRole["System"] = "system";
    OpenAiMessageRole["Assistant"] = "assistant";
    OpenAiMessageRole["User"] = "user";
})(OpenAiMessageRole || (OpenAiMessageRole = {}));
// https://docs.vapi.ai/api-reference/webhooks/server-message#request.body.message.EndOfCallReport.artifact.messages
export var VapiArtifactMessageRole;
(function (VapiArtifactMessageRole) {
    VapiArtifactMessageRole["System"] = "system";
    VapiArtifactMessageRole["User"] = "user";
    VapiArtifactMessageRole["Bot"] = "bot";
    VapiArtifactMessageRole["ToolCall"] = "tool_calls";
    VapiArtifactMessageRole["ToolCallResult"] = "tool_call_result";
})(VapiArtifactMessageRole || (VapiArtifactMessageRole = {}));
export var ToolMessageType;
(function (ToolMessageType) {
    ToolMessageType["RequestStart"] = "request-start";
    ToolMessageType["RequestComplete"] = "request-complete";
    ToolMessageType["RequestFailed"] = "request-failed";
    ToolMessageType["RequestResponseDelayed"] = "request-response-delayed";
})(ToolMessageType || (ToolMessageType = {}));
export var VoiceCallTool;
(function (VoiceCallTool) {
    VoiceCallTool["authenticateInboundPatient"] = "authenticateInboundPatient";
    VoiceCallTool["authenticateKnownPatient"] = "authenticateKnownPatient";
    VoiceCallTool["bookSelectedSlot"] = "bookSelectedSlot";
    VoiceCallTool["createPatient"] = "createPatient";
    VoiceCallTool["getAppointments"] = "getAppointments";
    VoiceCallTool["getEncounterDetails"] = "getEncounterDetails";
    VoiceCallTool["getPatient"] = "getPatient";
    VoiceCallTool["getEncounters"] = "getEncounters";
    VoiceCallTool["getInsurance"] = "getInsurance";
    VoiceCallTool["getOrders"] = "getOrders";
    VoiceCallTool["getPracticeDetails"] = "getPracticeDetails";
    VoiceCallTool["getTimeDifference"] = "getTimeDifference";
    VoiceCallTool["lookupPatient"] = "lookupPatient";
    VoiceCallTool["searchForOpenSlots"] = "searchForOpenSlots";
    VoiceCallTool["sendInboundAuthCode"] = "sendInboundAuthCode";
    VoiceCallTool["sendSms"] = "sendSms";
    VoiceCallTool["traverseDecisionTree"] = "traverseDecisionTree";
})(VoiceCallTool || (VoiceCallTool = {}));
export var VoiceCallTransferCallToolDestinationType;
(function (VoiceCallTransferCallToolDestinationType) {
    VoiceCallTransferCallToolDestinationType["Number"] = "number";
    VoiceCallTransferCallToolDestinationType["Sip"] = "sip";
})(VoiceCallTransferCallToolDestinationType || (VoiceCallTransferCallToolDestinationType = {}));
//https://docs.vapi.ai/call-forwarding#call-transfers-mode
export var VoiceCallTransferCallMode;
(function (VoiceCallTransferCallMode) {
    // The assistant forwards the call to the destination without any message or summary.
    VoiceCallTransferCallMode["BlindTransfer"] = "blind-transfer";
    // The assistant forwards the call to the destination and adds a SIP header X-Transfer-Summary to the call to include the summary.
    VoiceCallTransferCallMode["BlindTransferAddSummaryToSipHeader"] = "blind-transfer-add-summary-to-sip-header";
    // The assistant dials the destination, delivers the message to the destination party, connects the customer, and leaves the call.
    VoiceCallTransferCallMode["WarmTransferSayMessage"] = "warm-transfer-say-message";
    //The assistant dials the destination, provides a summary of the call to the destination party, connects the customer, and leaves the call.
    VoiceCallTransferCallMode["WarmTransferSaySummary"] = "warm-transfer-say-summary";
    //The assistant dials the destination, waits for the operator to speak, delivers the message to the destination party, and then connects the customer.
    VoiceCallTransferCallMode["WarmTransferWaitForOperatorToSpeakFirstAndThenSayMessage"] = "warm-transfer-wait-for-operator-to-speak-first-and-then-say-message";
    //The assistant dials the destination, waits for the operator to speak, provides a summary of the call to the destination party, and then connects the customer.
    VoiceCallTransferCallMode["WarmTransferWaitForOperatorToSpeakFirstAndThenSaySummary"] = "warm-transfer-wait-for-operator-to-speak-first-and-then-say-summary";
    // The assistant dials the destination, executes the twiml instructions on the destination call leg, connects the customer, and leaves the call
    VoiceCallTransferCallMode["WarmTransferTwiml"] = "warm-transfer-twiml";
    // The assistant puts the customer on hold, dials the destination, and if the destination answers (and is human), delivers a message or summary before connecting the customer.
    // If the destination is unreachable or not human (e.g., with voicemail detection), the assistant delivers the fallbackMessage to the customer and optionally ends the call
    VoiceCallTransferCallMode["WarmTransferExperimental"] = "warm-transfer-experimental";
    // The assistant dials the destination, sends an 10-digit call token via DTMF. Customer can then query notable's call api with from, to and token to get the call info.
    VoiceCallTransferCallMode["TransferWithDtmfCallToken"] = "transfer-with-dtmf-call-token";
    // The assistant forwards the call to the destination and adds a SIP header X-Notable-Token to the call to include the token.
    VoiceCallTransferCallMode["BlindTransferWithTokenInSipHeader"] = "blind-transfer-with-token-in-sip-header";
    // The assistant forwards the call to the destination and adds a SIP header X-Notable-VoiceCallId (UUID) to the call.
    VoiceCallTransferCallMode["BlindTransferWithVoiceCallIdInSipHeader"] = "blind-transfer-with-voice-call-id-in-sip-header";
})(VoiceCallTransferCallMode || (VoiceCallTransferCallMode = {}));
// Base artifact message schema for all message types based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/Artifact.ts#L35-L42
const BaseVoiceCallArtifactMessageSchema = z.looseObject({
    role: z.enum(VapiArtifactMessageRole),
    time: z.number(),
    secondsFromStart: z.number(),
});
// Schema for VoiceCallUserMessage based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/VoiceCallUserMessage.ts
export const VoiceCallUserMessageSchema = BaseVoiceCallArtifactMessageSchema.extend({
    role: z.literal(VapiArtifactMessageRole.User),
    message: z.string(),
    endTime: z.number(),
    duration: z.number().optional(),
});
// Schema for VoiceCallSystemMessage based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/VoiceCallSystemMessage.ts
export const VoiceCallSystemMessageSchema = BaseVoiceCallArtifactMessageSchema.extend({
    role: z.literal(VapiArtifactMessageRole.System),
    message: z.string(),
});
// Schema for VoiceCallBotMessage based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/VoiceCallBotMessage.ts
export const VoiceCallBotMessageSchema = BaseVoiceCallArtifactMessageSchema.extend({
    role: z.literal(VapiArtifactMessageRole.Bot),
    message: z.string(),
    endTime: z.number(),
    source: z.string().optional(),
    duration: z.number().optional(),
});
// Schema for VoiceCallToolCallMessage based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/VoiceCallToolCallMessage.ts
export const VoiceCallToolCallMessageSchema = BaseVoiceCallArtifactMessageSchema.extend({
    role: z.literal(VapiArtifactMessageRole.ToolCall),
    toolCalls: z.array(z.record(z.string(), z.unknown())),
    message: z.string(),
});
// Schema for VoiceCallToolCallResultMessage based on Vapi SDK types
// https://github.com/VapiAI/server-sdk-typescript/blob/main/src/api/types/VoiceCallToolCallResultMessage.ts
export const VoiceCallToolCallResultMessageSchema = BaseVoiceCallArtifactMessageSchema.extend({
    role: z.literal(VapiArtifactMessageRole.ToolCallResult),
    toolCallId: z.string(),
    name: z.string(),
    result: z.union([z.string(), z.record(z.string(), z.unknown())]),
    metadata: z.record(z.string(), z.unknown()).optional(),
    status: z.enum(VoiceApiStatus).optional(),
});
export const VoiceCallMessageSchema = z.discriminatedUnion('role', [
    VoiceCallUserMessageSchema,
    VoiceCallSystemMessageSchema,
    VoiceCallBotMessageSchema,
    VoiceCallToolCallMessageSchema,
    VoiceCallToolCallResultMessageSchema,
]);
//# sourceMappingURL=vapi.js.map